import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';

export default function HeroPreviewCard() {
  const { t } = useTranslation();
  const [percentA, setPercentA] = useState(0);
  const [percentB, setPercentB] = useState(0);

  // Animate the progress bars to give a dynamic feel when loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      setPercentA(85);
      setPercentB(60);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95, y: 30 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      style={{
        background: 'linear-gradient(135deg, rgba(30,41,59,0.8), rgba(15,23,42,0.9))',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        padding: '1.75rem',
        boxShadow: '0 25px 50px rgba(0,0,0,0.5), inset 0 1px 1px rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
        width: '100%',
        maxWidth: '360px',
        margin: '0 auto',
        fontFamily: 'Inter, system-ui, sans-serif',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Decorative Glow Elements */}
      <div style={{ position: 'absolute', top: '-40px', left: '-40px', width: '150px', height: '150px', background: 'rgba(99, 102, 241, 0.4)', filter: 'blur(60px)', borderRadius: '50%', pointerEvents: 'none' }}></div>
      <div style={{ position: 'absolute', bottom: '-40px', right: '-40px', width: '150px', height: '150px', background: 'rgba(236, 72, 153, 0.3)', filter: 'blur(60px)', borderRadius: '50%', pointerEvents: 'none' }}></div>
      <div className="hero-scanning-line" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)', zIndex: 10, animation: 'scan 2.5s infinite linear' }}></div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
        <h3 style={{ fontSize: '1.15rem', fontWeight: '800', color: '#f8fafc', margin: 0 }}>
          {t('home.hero_card.title')}
        </h3>
        <span style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#f8fafc', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.5px' }}>
          {t('home.hero_card.ai_badge')}
        </span>
      </div>
      <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '-1rem', position: 'relative', zIndex: 1 }}>
        {t('home.hero_card.ai_desc')}
      </div>

      {/* Section 1: AI Score Radar */}
      <div style={{
        padding: '1.25rem 1rem 0.5rem 1rem',
        background: 'rgba(0,0,0,0.2)',
        borderRadius: '16px',
        border: '1px solid rgba(255,255,255,0.05)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative', 
        zIndex: 1
      }}>
        <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.25rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', alignSelf: 'flex-start' }}>
          {t('home.hero_card.section1_title') || 'Vibe Radar'}
        </div>
        
        <div style={{ width: '100%', height: '190px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={[
              { subject: 'Noble', score: 85 },
              { subject: 'Cute', score: 65 },
              { subject: 'Sexy', score: 40 },
              { subject: 'Pure', score: 70 },
              { subject: 'Hip', score: 55 },
              { subject: 'Strong', score: 30 }
            ]}>
              <PolarGrid stroke="rgba(255,255,255,0.1)" />
              <PolarAngleAxis dataKey="subject" tick={{ fill: '#cbd5e1', fontSize: 10, fontWeight: 700 }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
              <Radar
                name="Score"
                dataKey="score"
                stroke="#818cf8"
                strokeWidth={2}
                fill="#6366f1"
                fillOpacity={0.4}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Section 2: Chemistry Prediction Example */}
      <div style={{
        padding: '1.5rem 1.25rem',
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '16px',
        border: '1px solid rgba(236,72,153,0.2)',
        position: 'relative', 
        zIndex: 1
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '1rem' }}>
          <span style={{ fontSize: '1rem' }}>💘</span>
          <div style={{ fontSize: '0.8rem', color: '#fbcfe8', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Chemistry Prediction
          </div>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <div style={{ fontSize: '0.9rem', color: '#e2e8f0', fontWeight: '500', lineHeight: '1.5' }}>
            <span style={{ color: '#ff6b8a', fontWeight: '700' }}>유저 A</span>님이 <span style={{ color: '#818cf8', fontWeight: '700' }}>유저 B</span>님에게 더 빠르게 끌릴 가능성이 높습니다.
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.5rem' }}>
            {/* User A to B */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '0.75rem', color: '#94a3b8', width: '45px', fontWeight: '700' }}>A → B</span>
              <div style={{ flex: 1, height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ 
                  position: 'absolute', top: 0, left: 0, height: '100%', 
                  width: `${percentA}%`, 
                  background: 'linear-gradient(90deg, #ff6b8a, #fb7185)', 
                  borderRadius: '4px',
                  transition: 'width 1.5s cubic-bezier(0.16, 1, 0.3, 1)',
                  boxShadow: '0 0 10px rgba(255,107,138,0.5)'
                }}></div>
              </div>
              <span style={{ fontSize: '0.8rem', color: '#ff6b8a', fontWeight: '800', width: '30px', textAlign: 'right' }}>{percentA}%</span>
            </div>
            
            {/* User B to A */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '0.75rem', color: '#94a3b8', width: '45px', fontWeight: '700' }}>B → A</span>
              <div style={{ flex: 1, height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ 
                  position: 'absolute', top: 0, left: 0, height: '100%', 
                  width: `${percentB}%`, 
                  background: 'linear-gradient(90deg, #818cf8, #a78bfa)', 
                  borderRadius: '4px',
                  transition: 'width 1.5s cubic-bezier(0.16, 1, 0.3, 1) 0.2s',
                  boxShadow: '0 0 10px rgba(129,140,248,0.5)'
                }}></div>
              </div>
              <span style={{ fontSize: '0.8rem', color: '#818cf8', fontWeight: '800', width: '30px', textAlign: 'right' }}>{percentB}%</span>
            </div>
          </div>
        </div>
      </div>
      
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes scan {
          0% { transform: translateY(-30px); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(450px); opacity: 0; }
        }
      `}} />
    </motion.div>
  );
}
